import { CodeStatus, PermitCategory, PermitType, SignatureType } from "../../common/enums";
import { formElement } from "../forms/FormElement";
import MaritimeEventFormState from "../forms/Permits/MaritimeEventFormState";
import Person, { Person_default, Person_toFormElement } from "../Person";
import Position, { Position_toPositionPickerRow } from "../Position";
import Signature, { Signature_empty } from "../Signature";
import { MaritimeEventType } from "../../common/enums";
import { permitContentVersions } from "../WorkPermit";

type MaritimeEvent = {
    id: string;
    entityId: number;
    remindMe: boolean;
    remindMeDate: Date;
    startDate: Date;
    endDate: Date;
    adminComment: string;
    contactPerson: Person;
    status: CodeStatus;
    sequenceIdentifier: string;
    description: string;
    permitName: PermitType.MaritimeEvent;
    positions: Position[];
    maritimeEventType?: MaritimeEventType;

    permitProviderSignature: Signature;
    expirationReminderEmail: string;
    expirationReminderPhoneNumber: string;
    version: number;
    hasAttachedFiles: boolean;
    entrepreneur: string;
    renewalOfPermitSequenceIdentifier: string;
    renewedByPermitSequenceIdentifier: string;
    createdByUserName: string;
    rejectionComment: string;
    initialStartDate: Date;
    extendedEndDate: Date;

    departmentId: number;

    contentVersion: number;

    canUpdate: boolean;
    canAccept: boolean;
    canManageAttachments: boolean;
    canManagePositions: boolean;
    canReject: boolean;
    canManageAdminComment: boolean;
    canRenew: boolean;
    canManagePositionPolygons: boolean;
};

export default MaritimeEvent;

export function MaritimeEvent_toFormState(maritimeEvent: MaritimeEvent): MaritimeEventFormState {
    const formState: MaritimeEventFormState = {
        adminComment: formElement(maritimeEvent.adminComment),
        contactPerson: Person_toFormElement(maritimeEvent.contactPerson),
        description: formElement(maritimeEvent.description),
        endDate: formElement(maritimeEvent.endDate),
        entrepreneur: formElement(maritimeEvent.entrepreneur),
        expirationReminderEmail: formElement(maritimeEvent.expirationReminderEmail),
        expirationReminderPhoneNumber: formElement(maritimeEvent.expirationReminderPhoneNumber),
        positions: maritimeEvent.positions.map(Position_toPositionPickerRow),
        remindMe: formElement(maritimeEvent.remindMe),
        remindMeDate: formElement(maritimeEvent.remindMeDate),
        startDate: formElement(maritimeEvent.startDate),
        maritimeEventType: formElement(maritimeEvent.maritimeEventType ?? MaritimeEventType.General)
    };

    return formState;
}

export function MaritimeEvent_normalize(maritimeEvent: MaritimeEvent): MaritimeEvent {
    const normalizedMaritimeEvent: MaritimeEvent = {
        adminComment: maritimeEvent.adminComment ?? "",
        canAccept: maritimeEvent.canAccept ?? false,
        canManageAttachments: maritimeEvent.canManageAttachments ?? false,
        canManagePositions: maritimeEvent.canManagePositions ?? false,
        canReject: maritimeEvent.canReject ?? false,
        canUpdate: maritimeEvent.canUpdate ?? false,
        contactPerson: maritimeEvent.contactPerson ?? Person_default(),
        createdByUserName: maritimeEvent.createdByUserName ?? "",
        departmentId: maritimeEvent.departmentId ?? 0,
        description: maritimeEvent.description ?? "",
        endDate: maritimeEvent.endDate ? new Date(maritimeEvent.endDate) : new Date(),
        entityId: maritimeEvent.entityId ?? 0,
        entrepreneur: maritimeEvent.entrepreneur ?? "",
        expirationReminderEmail: maritimeEvent.expirationReminderEmail ?? "",
        expirationReminderPhoneNumber: maritimeEvent.expirationReminderPhoneNumber ?? "",
        hasAttachedFiles: maritimeEvent.hasAttachedFiles ?? false,
        id: maritimeEvent.id ?? "",
        permitName: PermitType.MaritimeEvent,
        permitProviderSignature: maritimeEvent.permitProviderSignature ?? Signature_empty(SignatureType.PermitProvider),
        positions: maritimeEvent.positions ?? [],
        remindMe: maritimeEvent.remindMe ?? false,
        remindMeDate: maritimeEvent.remindMeDate ? new Date(maritimeEvent.remindMeDate) : new Date(),
        sequenceIdentifier: maritimeEvent.sequenceIdentifier ?? "",
        renewalOfPermitSequenceIdentifier: maritimeEvent.renewalOfPermitSequenceIdentifier ?? "",
        renewedByPermitSequenceIdentifier: maritimeEvent.renewedByPermitSequenceIdentifier ?? "",
        startDate: maritimeEvent.startDate ? new Date(maritimeEvent.startDate) : new Date(),
        status: maritimeEvent.status ?? CodeStatus.Archived,
        version: maritimeEvent.version ?? 0,
        rejectionComment: maritimeEvent.rejectionComment ?? "",
        canManageAdminComment: maritimeEvent.canManageAdminComment ?? false,
        canRenew: maritimeEvent.canRenew ?? false,
        extendedEndDate: maritimeEvent.extendedEndDate ? new Date(maritimeEvent.extendedEndDate) : new Date(),
        initialStartDate: maritimeEvent.initialStartDate ? new Date(maritimeEvent.initialStartDate) : new Date(),
        maritimeEventType: maritimeEvent.maritimeEventType ?? MaritimeEventType.General,
        canManagePositionPolygons: maritimeEvent.canManagePositionPolygons ?? false,
        contentVersion: maritimeEvent.contentVersion ?? Math.max(...Object.values(permitContentVersions))
    };

    return normalizedMaritimeEvent;
}
